import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFlowOwner } from "@/components/FlowAccessGate";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Sparkles, Twitter, Facebook, Instagram, Linkedin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getGeoData } from "@/utils/geolocation";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { trackAffiliateConversion, getAffiliateCookie } from "@/lib/affiliate-tracker";
import { trackRedditEvent } from "@/lib/reddit-pixel";

type Flow = {
  id: string;
  business_name: string;
  product_name: string;
  logo_url: string | null;
  product_image_url: string | null;
  waitlist_design?: {
    title: string;
    subtitle: string;
    description: string;
    ctaText: string;
    bgColor: string;
    bgImage: string;
    style: string;
    // Navbar options
    showNavbar?: boolean;
    navbarName?: string;
    navbarLinks?: Array<{
      label: string;
      url: string;
    }>;
    navbarSocial?: Array<{
      type: string;
      url: string;
    }>;
  };
};

export default function SignupFlow() {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const { isPaid } = useFlowOwner();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [flow, setFlow] = useState<Flow | null>(null);
  const [email, setEmail] = useState("");

  useEffect(() => {
    const fetchFlow = async () => {
      try {
        if (!flowId) return;
        
        const { data, error } = await supabase
          .from('flows')
          .select('*')
          .eq('id', flowId)
          .single();
        
        if (error) {
          throw error;
        }
        
        setFlow(data);
        recordFlowVisit();
      } catch (error) {
        toast.error('Failed to load flow data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFlow();
  }, [flowId]);

  const recordFlowVisit = async () => {
    if (!flowId) return;
    
    try {
      // Generate a unique key for this flow view
      const viewKey = `flow_view_${flowId}`;
      
      // Check if this user has already viewed this flow today
      const viewDate = new Date().toISOString().split('T')[0];
      const storedView = localStorage.getItem(viewKey);
      
      if (storedView === viewDate) {
        return;
      }
      
      localStorage.setItem(viewKey, viewDate);
      
      let userIdentifier = localStorage.getItem('flow_user_id');
      if (!userIdentifier) {
        userIdentifier = crypto.randomUUID();
        localStorage.setItem('flow_user_id', userIdentifier);
      }
      
      const geoData = await getGeoData();
      
      await supabase.from('flow_analytics').insert({
        flow_id: flowId,
        event_type: 'view',
        referrer: document.referrer || 'direct',
        is_embedded: window.self !== window.top,
        user_agent: navigator.userAgent,
        path: window.location.pathname,
        country: geoData?.country || 'Unknown',
        user_identifier: userIdentifier,
        view_date: viewDate
      });
    } catch (error) {
      // Failed to record view analytics
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (!email.trim()) {
        throw new Error('Please enter your email address');
      }

      if (!flowId) {
        throw new Error('Flow ID is missing');
      }

      // Check if email already exists in waitlist for this flow
      const { data: existingEntry, error: checkError } = await supabase
        .from('waitlist_entries')
        .select('id')
        .eq('flow_id', flowId)
        .eq('email', email.toLowerCase().trim())
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingEntry) {
        toast.success("You're already on the waitlist! We'll notify you when we launch.");
        navigate(`/thank-you/${flowId}`);
        return;
      }

      const geoData = await getGeoData();
      
      // Add to waitlist
      const { error: waitlistError } = await supabase.from('waitlist_entries').insert({
        flow_id: flowId,
        email: email.toLowerCase().trim(),
        user_agent: navigator.userAgent,
        referrer: document.referrer || 'direct',
        country: geoData?.country || 'Unknown',
        ip_address: '',  // This will be handled by Supabase
        metadata: {
          is_embedded: window.self !== window.top,
          path: window.location.pathname
        }
      });

      if (waitlistError) throw waitlistError;

      // Check if conversion already recorded for this email today
      const conversionKey = `flow_conversion_${flowId}_${email.toLowerCase().trim()}`;
      const lastConversion = localStorage.getItem(conversionKey);
      const today = new Date().toISOString().split('T')[0];

      if (!lastConversion || lastConversion !== today) {
        // Record analytics only if not converted today
        await supabase.from('flow_analytics').insert({
          flow_id: flowId,
          email: email.toLowerCase().trim(),
          event_type: 'conversion',
          referrer: document.referrer || 'direct',
          is_embedded: window.self !== window.top,
          user_agent: navigator.userAgent,
          path: window.location.pathname,
          country: geoData?.country || 'Unknown'
        });

        // Store conversion date
        localStorage.setItem(conversionKey, today);

        // Track affiliate conversion for waitlist signup
        const affiliateCode = getAffiliateCookie();
        if (affiliateCode) {
          try {
            await trackAffiliateConversion({
              userId: email.toLowerCase().trim(),
              orderId: `waitlist_${flowId}_${Date.now()}`,
              amount: 0, // Free waitlist signup
              planName: 'Waitlist Signup',
              planInterval: 'lifetime',
              affiliateCode: affiliateCode
            });
          } catch (conversionError) {
            // Don't fail the signup if tracking fails
          }
        }
      }

      // Track lead with Reddit Pixel + CAPI
      trackRedditEvent('Lead', { email: email.toLowerCase().trim() });

      toast.success("You've been added to the waitlist!");
      navigate(`/thank-you/${flowId}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to submit');
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Flow not found</h1>
          <p className="text-muted-foreground">The requested flow could not be found.</p>
        </div>
      </div>
    );
  }

  // Default waitlist design if none provided
  const waitlistDesign = flow.waitlist_design || {
    title: "Join the Waitlist",
    subtitle: `Be the first to know when ${flow.product_name} launches.`,
    description: "Get exclusive early access and updates.",
    ctaText: "Join Waitlist",
    bgColor: "#f8fafc",
    bgImage: "",
    style: "centered",
    showNavbar: false,
    navbarName: flow.business_name || flow.product_name,
    navbarLinks: [],
    navbarSocial: []
  };

  // Render the hero section layout
  if (waitlistDesign.style === "hero") {
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{
          backgroundColor: waitlistDesign.bgColor,
          backgroundImage: waitlistDesign.bgImage ? `url(${waitlistDesign.bgImage})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Navbar */}
        {waitlistDesign.showNavbar && (
          <div className="w-full py-4 px-6 flex items-center justify-between bg-white/80 backdrop-blur-sm border-b">
            <div className="flex items-center gap-2">
              {flow.logo_url && (
                <img 
                  src={flow.logo_url} 
                  alt={waitlistDesign.navbarName || flow.business_name || ""} 
                  className="h-8 w-auto"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <span className="font-medium">{waitlistDesign.navbarName || flow.business_name}</span>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-4">
                {waitlistDesign.navbarLinks?.map((link, index) => (
                  <a 
                    key={index} 
                    href={link.url || "#"} 
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    {link.label || "Link"}
                  </a>
                ))}
              </div>
              
              <div className="flex items-center gap-2">
                {waitlistDesign.navbarSocial?.map((social, index) => (
                  <a 
                    key={index} 
                    href={social.url || "#"} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {social.type === "twitter" && <Twitter className="h-4 w-4" />}
                    {social.type === "facebook" && <Facebook className="h-4 w-4" />}
                    {social.type === "instagram" && <Instagram className="h-4 w-4" />}
                    {social.type === "linkedin" && <Linkedin className="h-4 w-4" />}
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Hero Content */}
        <div className="flex-1 flex items-center justify-center p-6 md:p-12">
          <div className="max-w-6xl w-full mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  {waitlistDesign.title}
                </h1>
                
                <h2 className="text-xl md:text-2xl mb-6 text-gray-700">
                  {waitlistDesign.subtitle}
                </h2>
                
                <p className="mb-8 text-gray-600 text-lg">
                  {waitlistDesign.description}
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-12 flex-1"
                    />
                    <Button
                      type="submit"
                      className="h-12 px-6 bg-indigo-600 hover:bg-indigo-700 text-white"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Joining...
                        </>
                      ) : (
                        waitlistDesign.ctaText
                      )}
                    </Button>
                  </div>
                  
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>No spam, unsubscribe anytime</span>
                  </div>
                </form>
              </div>
              
              <div className="hidden md:flex items-center justify-center">
                {flow.product_image_url ? (
                  <img 
                    src={flow.product_image_url} 
                    alt={flow.product_name} 
                    className="max-w-full max-h-96 object-contain rounded-lg shadow-lg"
                  />
                ) : (
                  <div className="w-full h-80 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border flex items-center justify-center">
                    <div className="text-center p-6">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-2xl text-indigo-600">{flow.product_name?.charAt(0) || "P"}</span>
                      </div>
                      <h3 className="text-xl font-medium text-gray-800">{flow.product_name}</h3>
                      <p className="text-gray-500 mt-2">Coming soon</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {!isPaid && (
          <div className="py-4 text-center">
            <p className="text-xs text-gray-500">
              Powered by <a href="https://willtheyconvert.com" className="underline hover:text-gray-700">WillTheyConvert.com</a>
            </p>
          </div>
        )}
      </div>
    );
  }

  // Render the original layouts
  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundColor: waitlistDesign.bgColor,
        backgroundImage: waitlistDesign.bgImage ? `url(${waitlistDesign.bgImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className={cn(
        "w-full",
        waitlistDesign.style === "centered" && "max-w-lg",
        waitlistDesign.style === "split" && "max-w-4xl",
        waitlistDesign.style === "minimal" && "max-w-md",
        waitlistDesign.style === "gradient" && "max-w-lg"
      )}>
        <Card className={cn(
          "shadow-lg",
          waitlistDesign.style === "gradient" && "bg-gradient-to-r from-indigo-500/10 to-purple-500/10 backdrop-blur-sm border-0",
          waitlistDesign.style === "minimal" && "border-0 shadow-none bg-transparent"
        )}>
          <CardContent className={cn(
            "p-8",
            waitlistDesign.style === "split" && "grid grid-cols-1 md:grid-cols-2 gap-6"
          )}>
            <div className={cn(
              waitlistDesign.style === "centered" && "text-center",
              waitlistDesign.style === "split" && "flex flex-col justify-center"
            )}>
              {flow.logo_url && waitlistDesign.style !== "minimal" && (
                <div className={cn(
                  "mb-6",
                  waitlistDesign.style === "centered" && "flex justify-center"
                )}>
                  <img 
                    src={flow.logo_url} 
                    alt={flow.business_name || flow.product_name} 
                    className="h-12 w-auto object-contain"
                  />
                </div>
              )}
              
              <h2 className={cn(
                "font-bold mb-2",
                waitlistDesign.style === "centered" && "text-3xl",
                waitlistDesign.style === "split" && "text-3xl",
                waitlistDesign.style === "minimal" && "text-4xl",
                waitlistDesign.style === "gradient" && "text-3xl"
              )}>
                {waitlistDesign.title}
              </h2>
              
              <h3 className={cn(
                "mb-3",
                waitlistDesign.style === "centered" && "text-xl text-muted-foreground",
                waitlistDesign.style === "split" && "text-xl text-muted-foreground",
                waitlistDesign.style === "minimal" && "text-2xl",
                waitlistDesign.style === "gradient" && "text-xl font-medium"
              )}>
                {waitlistDesign.subtitle}
              </h3>
              
              <p className="mb-6 text-muted-foreground">
                {waitlistDesign.description}
              </p>
            </div>

            <form onSubmit={handleSubmit} className={cn(
              "space-y-4",
              waitlistDesign.style === "split" && "flex flex-col justify-center"
            )}>
              <div className="space-y-2">
                {waitlistDesign.style !== "minimal" && (
                  <Label htmlFor="email">Email address</Label>
                )}
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={cn(
                    "h-12",
                    waitlistDesign.style === "minimal" && "text-lg"
                  )}
                />
              </div>

              <Button
                type="submit"
                className={cn(
                  "w-full h-12",
                  waitlistDesign.style === "gradient" 
                    ? "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white" 
                    : "bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white"
                )}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Joining waitlist...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    {waitlistDesign.ctaText}
                  </>
                )}
              </Button>

              <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
                <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>No spam, unsubscribe anytime</span>
              </div>
            </form>
          </CardContent>
        </Card>

        {!isPaid && (
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              Powered by <a href="https://willtheyconvert.com" className="underline hover:text-gray-700">WillTheyConvert.com</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
} 